import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useChatStore } from '../store/chatStore';
import { roomsApi, messagesApi } from '../lib/api';
import { wsClient } from '../lib/websocket';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import VideoCall from '../components/VideoCall';
import { AnonymousUserSettings } from '../components/AnonymousUserSettings';

export default function ChatRoomPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, anonymousUser, isAuthenticated, isAnonymous, initializeAnonymous } = useAuthStore();
  const { currentRoom, messages, setCurrentRoom, setMessages, addMessage } = useChatStore();
  const [messageInput, setMessageInput] = useState('');
  const [roomUsers, setRoomUsers] = useState<Array<{ id: string; username: string }>>([]);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize anonymous user if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      initializeAnonymous();
    }
  }, [isAuthenticated, initializeAnonymous]);

  useEffect(() => {
    if (!id) return;

    const loadRoom = async () => {
      try {
        const room = await roomsApi.get(id);
        setCurrentRoom(room);
        
        const roomMessages = await messagesApi.list(id);
        setMessages(roomMessages);
        
        // Connect WebSocket and join room
        const token = localStorage.getItem('token');
        if (token) {
          // Authenticated user
          if (!wsClient.isConnected()) {
            wsClient.connect(token);
          }
        } else if (isAnonymous && anonymousUser) {
          // Anonymous user
          if (!wsClient.isConnected()) {
            wsClient.connect(undefined, anonymousUser.id, anonymousUser.username);
          }
        } else {
          // Fallback: try to initialize anonymous if not already done
          if (!isAuthenticated) {
            initializeAnonymous();
          }
        }
        
        // Join room after connection is established
        const joinRoom = () => {
          if (wsClient.isConnected()) {
            wsClient.joinRoom(id);
          } else {
            // Retry after a short delay (max 5 seconds)
            const retries = (joinRoom as any).retries || 0;
            if (retries < 10) {
              (joinRoom as any).retries = retries + 1;
              setTimeout(joinRoom, 500);
            } else {
              console.error('Failed to join room after multiple attempts');
            }
          }
        };
        setTimeout(joinRoom, 500);
      } catch (error) {
        console.error('Failed to load room:', error);
        navigate('/');
      }
    };

    loadRoom();

    const handleNewMessage = (message: any) => {
      console.log('Received new message:', message, 'Current room id:', id);
      if (message.room_id === id) {
        console.log('Adding message to store');
        addMessage(message);
      } else {
        console.log('Message room_id mismatch:', message.room_id, '!==', id);
      }
    };

    const handleUserJoined = (data: { userId: string; username: string }) => {
      setRoomUsers((prev) => {
        if (prev.find((u) => u.id === data.userId)) {
          return prev;
        }
        return [...prev, { id: data.userId, username: data.username }];
      });
    };

    const handleUserLeft = (data: { userId: string }) => {
      setRoomUsers((prev) => prev.filter((u) => u.id !== data.userId));
    };

    // Set up WebSocket listeners - works for both authenticated and anonymous users
    // Set up listeners immediately (they'll work once socket connects)
    wsClient.onMessage(handleNewMessage);
    wsClient.onUserJoined(handleUserJoined);
    wsClient.onUserLeft(handleUserLeft);

    return () => {
      wsClient.offMessage(handleNewMessage);
      wsClient.offUserJoined(handleUserJoined);
      wsClient.offUserLeft(handleUserLeft);
      if (id) {
        wsClient.leaveRoom(id);
      }
    };
  }, [id, setCurrentRoom, setMessages, addMessage, navigate, isAuthenticated, isAnonymous, anonymousUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || !id) return;
    
    if (!isAuthenticated && !isAnonymous) {
      console.warn('Cannot send message: user not authenticated');
      return;
    }

    if (!wsClient.isConnected()) {
      console.warn('Cannot send message: WebSocket not connected');
      // Try to reconnect
      const token = localStorage.getItem('token');
      if (token) {
        wsClient.connect(token);
      } else if (isAnonymous && anonymousUser) {
        wsClient.connect(undefined, anonymousUser.id, anonymousUser.username);
      }
      // Wait a bit and try again
      setTimeout(() => {
        if (wsClient.isConnected()) {
          wsClient.sendMessage(id, messageInput);
          setMessageInput('');
        } else {
          alert('Unable to connect to chat server. Please refresh the page.');
        }
      }, 1000);
      return;
    }

    console.log('Sending message:', messageInput, 'to room:', id);
    wsClient.sendMessage(id, messageInput);
    setMessageInput('');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center relative">
          {/* Left: Back button */}
          <div className="w-1/3">
            <Button variant="ghost" onClick={() => navigate('/')}>
              ← Back
            </Button>
          </div>
          
          {/* Center: Logo */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-3">
            <img 
              src="/assets/TheWorldBridger.png" 
              alt="TheWorldBridger Logo" 
              className="h-12 w-auto"
            />
            <h1 className="text-2xl font-bold">{currentRoom?.name || 'Loading...'}</h1>
          </div>
          
          {/* Right: User actions */}
          <div className="w-1/3 flex justify-end">
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                {!isAnonymous && user && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowVideoCall(!showVideoCall)}
                    title="Start video call"
                  >
                    {showVideoCall ? 'Hide Video' : 'Start Video Call'}
                  </Button>
                )}
                <span className="text-sm text-muted-foreground">
                  {isAnonymous ? anonymousUser?.username : user?.username}
                </span>
              </div>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm">Login to Chat</Button>
              </Link>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 container mx-auto px-4 py-4 flex flex-col max-w-4xl">
        {/* Anonymous User Settings */}
        {isAnonymous && <AnonymousUserSettings />}
        
        {/* Video Call Component - Only show when toggled */}
        {isAuthenticated && !isAnonymous && user && showVideoCall && (
          <VideoCall roomId={id || ''} userId={user.id} onEndCall={() => setShowVideoCall(false)} />
        )}
        
        <Card className="flex-1 flex flex-col p-0 overflow-hidden">
          {/* Messages Window */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background min-h-[400px] max-h-[calc(100vh-250px)]">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <p>No messages yet. Be the first to say something!</p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    (message.user_id && message.user_id === user?.id) || 
                    (message.user_id === null && isAnonymous && message.anonymous_name === anonymousUser?.username)
                      ? 'justify-end' 
                      : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 shadow-sm ${
                      (message.user_id && message.user_id === user?.id) || 
                      (message.user_id === null && isAnonymous && message.anonymous_name === anonymousUser?.username)
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <div className="text-xs font-semibold mb-1 opacity-90">
                      {message.user?.username || message.anonymous_name || 'Unknown'}
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words">{message.content}</div>
                    <div className="text-xs opacity-70 mt-1">
                      {new Date(message.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Divider */}
          <div className="border-t"></div>
          {/* Chat Input Box */}
          <div className="p-4 bg-muted/50">
            {isAuthenticated ? (
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <Button type="submit" disabled={!messageInput.trim()}>
                  Send
                </Button>
              </form>
            ) : (
              <div className="p-4 text-center border rounded-lg bg-background">
                <p className="text-sm text-muted-foreground mb-2">
                  Connecting...
                </p>
              </div>
            )}
          </div>
        </Card>
      </main>
      
    </div>
  );
}

